import React from 'react';
import { FONT } from '../constants';
import { C } from '../styles';
import type { Field } from '../types';

interface Props {
  field: Field;
  value: unknown;
  error?: string;
  onChange: (v: string) => void;
}

export default function FieldEditor({ field, value, error, onChange }: Props) {
  const v = value == null ? '' : (value as string);
  const borderColor = error ? C.err : C.line;
  const common: React.CSSProperties = {
    border: '1px solid ' + borderColor,
    borderRadius: 10,
    padding: '10px 12px',
    fontFamily: FONT,
    fontSize: 14,
    color: C.ink,
    background: '#fff',
    width: '100%',
  };

  let control: React.ReactNode;
  if (field.t === 'select') {
    control = (
      <select
        value={v}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...common, cursor: 'pointer' }}
      >
        <option value="">Select</option>
        {(field.opts || []).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  } else if (field.t === 'wloc-select') {
    const locs = field.workLocations || [];
    control = (
      <select
        value={v}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...common, cursor: 'pointer' }}
      >
        <option value="">Select a location</option>
        {locs.map((loc) => (
          <option key={loc.id} value={loc.id}>
            {loc.name || '(Unnamed location)'}
          </option>
        ))}
      </select>
    );
  } else if (field.t === 'money') {
    control = (
      <div style={{ position: 'relative' }}>
        <span style={{
          position: 'absolute', left: 12, top: '50%',
          transform: 'translateY(-50%)',
          color: C.muted, fontSize: 14,
        }}>$</span>
        <input
          value={v}
          inputMode="decimal"
          placeholder="0.00"
          onChange={(e) => onChange(e.target.value)}
          style={{ ...common, paddingLeft: 24 }}
        />
      </div>
    );
  } else {
    const type =
      field.t === 'date' ? 'date'
      : field.t === 'email' ? 'email'
      : field.t === 'tel' ? 'tel'
      : field.t === 'number' ? 'number'
      : 'text';
    control = (
      <input
        type={type}
        value={v}
        placeholder={field.placeholder || (field.t === 'date' ? '' : 'Not set')}
        onChange={(e) => onChange(e.target.value)}
        style={common}
      />
    );
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      gridColumn: field.full ? '1 / -1' : 'auto',
    }}>
      <label style={{
        fontSize: 11, fontWeight: 700,
        letterSpacing: '0.04em', textTransform: 'uppercase',
        color: '#12262B', marginBottom: 6,
      }}>
        {field.l}
        {field.req && <span style={{ color: C.amber, marginLeft: 3 }}>*</span>}
      </label>
      {control}
      {field.help && !error && (
        <div style={{ marginTop: 6, fontSize: 12, color: C.muted, lineHeight: 1.45 }}>
          {field.help}
          {field.helpLink && (
            <>
              {' '}
              <a
                href={field.helpLink.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: C.tealInk, textDecoration: 'none', fontWeight: 600, marginLeft: 4 }}
              >
                {field.helpLink.label} ↗
              </a>
            </>
          )}
        </div>
      )}
      {error && (
        <div style={{
          marginTop: 6, fontSize: 12, color: C.err,
          display: 'flex', alignItems: 'center', gap: 5,
        }}>
          <span style={{ fontWeight: 700 }}>!</span> {error}
        </div>
      )}
    </div>
  );
}